import { motion } from "framer-motion";
import CountUp from "react-countup";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Briefcase, 
  TrendingUp, 
  DollarSign, 
  ShoppingCart, 
  Download, 
  Clock, 
  Award,
  Target
} from "lucide-react";

const StatCard = ({ icon: Icon, value, label, suffix = "", prefix = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Parse numeric value
  const numericValue = parseFloat(value?.replace(/[^0-9.]/g, "")) || 0;
  const hasPlus = value?.includes("+");
  const hasMinus = value?.includes("-");

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay }}
      className="stat-card"
      data-testid={`stat-card-${label?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex justify-center mb-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#007AFF]/20 to-[#00C853]/20 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#007AFF]" />
        </div>
      </div>
      <div className="value">
        {isInView && (
          <>
            {prefix}
            {hasMinus && "-"}
            <CountUp end={numericValue} duration={2.5} />
            {suffix}
            {hasPlus && "+"}
          </>
        )}
      </div>
      <div className="label">{label}</div>
    </motion.div>
  );
};

const Stats = ({ stats }) => {
  const statsData = [
    { icon: Briefcase, value: stats?.projects_completed || "50+", label: "Clients Served", suffix: "" },
    { icon: TrendingUp, value: stats?.average_roas || "3.5x", label: "Average ROAS", suffix: "x" },
    { icon: DollarSign, value: stats?.managed_spends || "$50M+", label: "Managed Media Spends", prefix: "$", suffix: "M" },
    { icon: ShoppingCart, value: stats?.orders_increase || "+107%", label: "Orders Increase", suffix: "%" },
    { icon: Target, value: stats?.cpo_reduction || "-70%", label: "CPO Reduction", suffix: "%" },
    { icon: Download, value: stats?.app_installs || "1M+", label: "App Installs", suffix: "M" },
    { icon: Clock, value: stats?.experience_years || "12+", label: "Years Experience", suffix: "" },
    { icon: Award, value: stats?.job_success_rate || "100%", label: "Client Satisfaction", suffix: "%" },
  ];

  return (
    <section className="py-16 md:py-20 border-t border-[#30363D]" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Proven <span className="gradient-text">Results</span>
          </h2>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto">
            Numbers that speak louder than words. Our track record of delivering exceptional 
            performance for e-commerce and app marketing clients.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statsData.map((stat, index) => (
            <StatCard
              key={stat.label}
              icon={stat.icon}
              value={stat.value}
              label={stat.label}
              suffix={stat.suffix}
              prefix={stat.prefix}
              delay={index * 0.1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;
